import { useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { TrackerCreateOrUpdateInput } from "@/v2/entities/tracker/Tracker.api";
import TrackerForm from "@/v2/features/tracker/TrackerForm";
import { LoadingState } from "@/features/tracker/parts/LoadingState";
import { toast } from "sonner";
import { z } from "zod";

export function useTrackerId() {
  const { trackerId } = useParams();
  return z.string().min(1).parse(trackerId);
}

export default function UpdateTrackerPage() {
  const trackerId = useTrackerId();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getTracker, updateTracker } = useTrackerApi();

  // Determine where to navigate back to based on where user came from
  const getBackNavigationPath = useCallback(() => {
    const from = location.state?.from;
    if (from === "details") {
      return `/trackers/${trackerId}`;
    }
    // Default to list page (from === 'list' or no state)
    return "/trackers";
  }, [location.state?.from, trackerId]);

  const { data: tracker, isLoading: isLoadingTracker } = useQuery({
    queryKey: ["trackers", trackerId],
    queryFn: () => getTracker(trackerId!),
    enabled: !!trackerId,
  });

  const { mutate: updateTrackerMutation, isLoading: isUpdatingTracker } =
    useMutation({
      mutationKey: ["updateTracker"],
      mutationFn: (data: TrackerCreateOrUpdateInput) =>
        updateTracker(trackerId!, data),
      onSuccess: () => {
        queryClient.invalidateQueries(["trackers"]);
        toast.success("Tracker updated successfully");
        navigate(getBackNavigationPath());
      },
      onError: (error: any) => {
        toast.error(error?.message || "Failed to update tracker");
      },
    });

  const handleSubmit = useCallback(
    async (data: TrackerCreateOrUpdateInput) => {
      if (!trackerId) return;

      updateTrackerMutation(data);
    },
    [updateTrackerMutation]
  );

  const handleCancel = useCallback(() => {
    navigate(getBackNavigationPath());
  }, [navigate, getBackNavigationPath]);

  if (isLoadingTracker) {
    return <LoadingState message="Loading tracker..." />;
  }

  if (!tracker) {
    // Redirect to trackers list if tracker is not found
    navigate("/trackers");
    return null;
  }

  return (
    <TrackerForm
      mode="update"
      initialData={tracker}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      loading={isUpdatingTracker}
    />
  );
}
